import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";

type Row = {
  page_key: string;
  path: string;
  section: string | null;
  campaign: string | null;
  source: string | null;
  created_at: string;
};

const RANGES = [
  { days: 7, label: "7 dní" },
  { days: 30, label: "30 dní" },
  { days: 90, label: "90 dní" },
] as const;

const SECTION_LABELS: Record<string, string> = {
  blog: "Blog",
  info: "Informační stránky",
  legal: "Právní informace",
};

/** Superadmin overview of public page views (blog, info pages, legal pages). */
export function ContentViewsTab() {
  const [days, setDays] = useState<number>(30);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async (rangeDays: number) => {
    setLoading(true);
    const since = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from("content_page_views")
      .select("page_key, path, section, campaign, source, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000);
    setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  useEffect(() => {
    void load(days);
  }, [days]);

  const bySection = new Map<string, Map<string, { path: string; count: number; fromNewsletter: number }>>();
  for (const r of rows) {
    const section = r.section ?? "other";
    if (!bySection.has(section)) bySection.set(section, new Map());
    const pages = bySection.get(section)!;
    const cur = pages.get(r.page_key);
    const fromNewsletter = r.source === "newsletter" || r.source === "brevo" ? 1 : 0;
    if (cur) {
      cur.count += 1;
      cur.fromNewsletter += fromNewsletter;
    } else {
      pages.set(r.page_key, { path: r.path, count: 1, fromNewsletter });
    }
  }
  const sections = [...bySection.entries()].sort(
    (a, b) =>
      [...b[1].values()].reduce((s, p) => s + p.count, 0) -
      [...a[1].values()].reduce((s, p) => s + p.count, 0),
  );
  const newsletterTotal = rows.filter((r) => r.source === "newsletter" || r.source === "brevo").length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg">Návštěvy stránek (blog, info, právní)</CardTitle>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? "default" : "outline"}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => void load(days)} aria-label="Obnovit">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Načítám…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">
            Za zvolené období zatím nemáme žádné návštěvy těchto stránek.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Celkem <strong>{rows.length}</strong> návštěv za posledních {days} dní, z toho{" "}
              <strong>{newsletterTotal}</strong> z newsletteru.
            </p>
            <div className="space-y-6">
              {sections.map(([section, pages]) => {
                const sorted = [...pages.entries()].sort((a, b) => b[1].count - a[1].count);
                const max = sorted[0]?.[1].count ?? 1;
                return (
                  <div key={section}>
                    <h3 className="text-sm font-semibold mb-2">
                      {SECTION_LABELS[section] ?? section}
                    </h3>
                    <ul className="space-y-3">
                      {sorted.map(([key, info]) => (
                        <li key={key}>
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="font-medium">
                              {key}{" "}
                              <span className="text-muted-foreground font-normal">{info.path}</span>
                            </span>
                            <span className="tabular-nums font-semibold">
                              {info.count}
                              {info.fromNewsletter > 0 && (
                                <span className="ml-2 text-xs font-normal text-muted-foreground">
                                  ({info.fromNewsletter} z newsletteru)
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${Math.max(4, (info.count / max) * 100)}%` }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
